
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { UserRole } from "@/hooks/useUserRoles";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[]; // Updated to use UserRole type
}

const ProtectedRoute = ({ children, requiredRoles = [] }: ProtectedRouteProps) => {
  const { user, isLoading, isSupabaseReady, hasRole } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Monitor authentication state
  }, [user, isLoading, isSupabaseReady]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-gray-200 h-12 w-12 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (!isSupabaseReady) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Configuration Error</AlertTitle>
          <AlertDescription>
            Supabase is not properly configured. Please contact the administrator to resolve this issue.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!user) {
    // Redirect to login and remember where the user was trying to go
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role requirements if specified
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));
    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to access this page. Please contact your administrator.
            </AlertDescription>
          </Alert>
        </div>
      );
    }
  }

  // Simply return children since SidebarProvider is now handled at the route level
  return <>{children}</>;
};

export default ProtectedRoute;
